import React, {
  useEffect, useRef, useCallback, useImperativeHandle, forwardRef,
} from 'react';
import * as THREE from 'three';
import { AppState, TriangulationResult } from '../types';
import { buildTopGeometry, buildBottomGeometry, buildSidesGeometry } from '../utils/sheetGeometry';
import { drawGridOverlay, GridOverlaySettings } from '../utils/gridOverlay';
import { getOpeningBoundaryEdges } from '../utils/distortions';

export interface Viewport3DHandle {
  resetView:           () => void;
  updateSimPositions:  (xPosMm: number[], yPosMm: number[], zPosMm: number[]) => void;
  resetSimPositions:   () => void;
}

interface Props {
  appState:       AppState;
  textureDataUrl: string | null;
  triangulation:  TriangulationResult | null;
}

// Domyślna pozycja kamery
const DEF_THETA  = 0.5;   // kąt poziomy startowy (użyty do obliczenia kwaterniona)
const DEF_PHI    = 1.05;  // kąt pionowy startowy (od osi Y)
const DEF_RADIUS = 6.0;   // dla arkusza referencyjnego 300×300 mm

// Referencyjna przekątna arkusza 300×300 mm (w mm) — względem niej skalujemy zakres zoomu.
const REF_DIAG_MM = Math.hypot(300, 300);   // ≈ 424.3 mm

/**
 * Oblicza kwaternion domyślnej orientacji kamery (odpowiadający DEF_THETA, DEF_PHI).
 * Kamera startuje w kierunku (0,0,1) i jest obracana:
 *   1. wokół osi Y o kąt DEF_THETA (poziomo)
 *   2. wokół lokalnej osi X kamery o (DEF_PHI - π/2) (pionowo)
 */
function defaultOrbitQuat(): THREE.Quaternion {
  const qy = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(0, 1, 0), DEF_THETA,
  );
  const localRight = new THREE.Vector3(1, 0, 0).applyQuaternion(qy);
  const qx = new THREE.Quaternion().setFromAxisAngle(
    localRight, DEF_PHI - Math.PI / 2,
  );
  return qx.multiply(qy); // qy stosowane do wektora jako pierwsze, potem qx
}

const Viewport3D = forwardRef<Viewport3DHandle, Props>(
  ({ appState, textureDataUrl, triangulation }, ref) => {
  const mountRef    = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef   = useRef<THREE.PerspectiveCamera | null>(null);
  const sceneRef    = useRef<THREE.Scene | null>(null);
  const sheetGroupRef = useRef<THREE.Object3D | null>(null);
  const frameRef    = useRef<number>(0);

  // Materiały tworzone raz w init, aktualizowane przez osobny useEffect
  const topMatRef    = useRef<THREE.MeshStandardMaterial | null>(null);
  const bottomMatRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const sidesMatRef  = useRef<THREE.MeshStandardMaterial | null>(null);

  // Atrybuty pozycji dla aktualizacji symulacji
  const topPosRef        = useRef<THREE.BufferAttribute | null>(null);
  const bottomPosRef     = useRef<THREE.BufferAttribute | null>(null);
  const topGeoRef        = useRef<THREE.BufferGeometry | null>(null);
  const bottomGeoRef     = useRef<THREE.BufferGeometry | null>(null);
  const thicknessRef     = useRef(appState.thicknessMm);
  // Kopia pozycji startowych (do resetu po symulacji)
  const initTopPosRef    = useRef<Float32Array | null>(null);
  const initBottomPosRef = useRef<Float32Array | null>(null);
  // Ostatnie pozycje symulacji – re-aplikowane po każdej przebudowie geometrii
  const simPosRef = useRef<{ x: number[]; y: number[]; z: number[] } | null>(null);
  // Żółty kontur aktywnego otworu
  const outlineRef = useRef<THREE.LineSegments | null>(null);

  const orbit = useRef({
    quat:     defaultOrbitQuat(),        // orientacja kamery jako kwaternion
    radius:   DEF_RADIUS,
    target:   new THREE.Vector3(),       // punkt celowania (pan offset)
    dragging: false,                     // lewy przycisk – obracanie
    panning:  false,                     // środkowy przycisk – przesuwanie
    lastX:    0, lastY: 0,
  });

  const applyCamera = useCallback(() => {
    const cam = cameraRef.current;
    const o   = orbit.current;
    if (!cam) return;
    // Pozycja: obróć wektor (0,0,radius) kwaternionem, dodaj target (pan)
    const pos = new THREE.Vector3(0, 0, o.radius).applyQuaternion(o.quat);
    cam.position.copy(o.target).add(pos);
    // Wektor "góra" kamery zmienia się wraz z orientacją (umożliwia obrót za bieguny)
    cam.up.set(0, 1, 0).applyQuaternion(o.quat);
    cam.lookAt(o.target);
  }, []);

  // Aktualizuj grubość w refie gdy zmieni się w appState
  useEffect(() => { thicknessRef.current = appState.thicknessMm; }, [appState.thicknessMm]);

  useImperativeHandle(ref, () => ({
    resetView: () => {
      const scale = Math.hypot(appState.materialWidth, appState.materialHeight) / REF_DIAG_MM;
      orbit.current.quat.copy(defaultOrbitQuat());
      orbit.current.radius = DEF_RADIUS * scale;
      orbit.current.target.set(0, 0, 0);
      applyCamera();
    },

    updateSimPositions: (xPosMm: number[], yPosMm: number[], zPosMm: number[]) => {
      // Zapamiętaj pozycje do re-aplikacji po przebudowie geometrii
      simPosRef.current = { x: xPosMm, y: yPosMm, z: zPosMm };
      const topPos    = topPosRef.current;
      const bottomPos = bottomPosRef.current;
      const topGeo    = topGeoRef.current;
      const bottomGeo = bottomGeoRef.current;
      if (!topPos || !bottomPos || !topGeo || !bottomGeo) return;

      const S = 0.01; // mm → Three.js (1 unit = 100 mm)
      const t = thicknessRef.current * S;
      const nOrig = topGeo.userData.numOrigVerts;
      const boundaryDupIdx = topGeo.userData.boundaryDupIdx as Int32Array;

      // 1. Aktualizuj pozycje górnej powierzchni dla oryginalnych wierzchołków (0...nOrig-1)
      const hasXY = xPosMm.length === zPosMm.length && yPosMm.length === zPosMm.length;
      const count = Math.min(zPosMm.length, nOrig);
      for (let i = 0; i < count; i++) {
        const z = zPosMm[i] * S;
        if (hasXY) {
          topPos.setXYZ(i, xPosMm[i] * S, yPosMm[i] * S, z);
        } else {
          topPos.setZ(i, z);
        }
      }

      // 2. Synchronizuj duplikaty dla górnej powierzchni (wierzchołki graniczne płaskiej części)
      for (let i = 0; i < nOrig; i++) {
        const di = boundaryDupIdx[i];
        if (di >= 0) {
          topPos.setXYZ(di, topPos.getX(i), topPos.getY(i), topPos.getZ(i));
        }
      }
      topPos.needsUpdate = true;

      // 3. Oblicz normalne dla górnej powierzchni (uwzględniając odkształcenie)
      topGeo.computeVertexNormals();
      const topNormals = topGeo.attributes.normal as THREE.BufferAttribute;

      // 4. Aktualizuj dolną powierzchnię używając wektorów normalnych
      const p = new THREE.Vector3();
      const n1 = new THREE.Vector3();
      const n2 = new THREE.Vector3();

      for (let i = 0; i < nOrig; i++) {
        p.fromBufferAttribute(topPos, i);
        n1.fromBufferAttribute(topNormals, i);

        const di = boundaryDupIdx[i];
        if (di >= 0) {
          // Wierzchołek graniczny: uśrednij normalną z części wydmuchiwanej (i) i płaskiej (di)
          n2.fromBufferAttribute(topNormals, di);
          n1.add(n2).normalize();
          
          // Przesuń oba wierzchołki (na dole spotkają się w tym samym punkcie)
          p.addScaledVector(n1, -t);
          bottomPos.setXYZ(i, p.x, p.y, p.z);
          bottomPos.setXYZ(di, p.x, p.y, p.z);
        } else {
          // Zwykły wierzchołek (kopułka lub wnętrze płaskiego arkusza)
          p.addScaledVector(n1, -t);
          bottomPos.setXYZ(i, p.x, p.y, p.z);
        }
      }
      bottomPos.needsUpdate = true;

      // Opcjonalnie oblicz normalne dla dolnej powierzchni (dla poprawnego oświetlenia)
      bottomGeo.computeVertexNormals();
    },

    resetSimPositions: () => {
      simPosRef.current = null;
      const topPos    = topPosRef.current;
      const bottomPos = bottomPosRef.current;
      const topGeo    = topGeoRef.current;
      const bottomGeo = bottomGeoRef.current;
      const initTop   = initTopPosRef.current;
      const initBot   = initBottomPosRef.current;
      if (!topPos || !bottomPos || !initTop || !initBot) return;
      (topPos.array as Float32Array).set(initTop);
      (bottomPos.array as Float32Array).set(initBot);
      topPos.needsUpdate    = true;
      bottomPos.needsUpdate = true;
      if (topGeo)    topGeo.computeVertexNormals();
      if (bottomGeo) bottomGeo.computeVertexNormals();
    },
  }), [applyCamera]);

  // Inicjalizacja sceny Three.js (raz)
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x6e6e6e);
    sceneRef.current = scene;

    const cam = new THREE.PerspectiveCamera(
      45, mount.clientWidth / mount.clientHeight, 0.1, 1000,
    );
    cameraRef.current = cam;
    applyCamera();

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Oświetlenie
    const ambient = new THREE.AmbientLight(0x404060, 1.6);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 1.4);
    dir.position.set(4, 6, 4);
    scene.add(dir);

    // Utwórz materiały (bez geometrii – zarządzane przez osobny useEffect)
    topMatRef.current = new THREE.MeshStandardMaterial({
      color: 0x888888, side: THREE.FrontSide, roughness: 0.7, metalness: 0.1,
    });
    bottomMatRef.current = new THREE.MeshStandardMaterial({
      color: 0x888888, side: THREE.FrontSide, roughness: 0.7, metalness: 0.1,
    });
    sidesMatRef.current = new THREE.MeshStandardMaterial({
      color: 0x888888, side: THREE.DoubleSide, roughness: 0.7, metalness: 0.1,
    });

    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      renderer.render(scene, cam);
    };
    animate();

    const ro = new ResizeObserver(() => {
      if (!mount) return;
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(frameRef.current);
      ro.disconnect();
      if (outlineRef.current) {
        scene.remove(outlineRef.current);
        outlineRef.current.geometry.dispose();
        (outlineRef.current.material as THREE.Material).dispose();
        outlineRef.current = null;
      }
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      renderer.dispose();
      topMatRef.current?.dispose();    topMatRef.current    = null;
      bottomMatRef.current?.dispose(); bottomMatRef.current = null;
      sidesMatRef.current?.dispose();  sidesMatRef.current  = null;
    };
  }, []); // init raz – materiały i scena

  // Budowanie geometrii arkusza (przebudowa gdy zmieni się triangulacja lub wymiary)
  useEffect(() => {
    const scene    = sceneRef.current;
    const topMat    = topMatRef.current;
    const bottomMat = bottomMatRef.current;
    const sidesMat  = sidesMatRef.current;
    if (!scene || !topMat || !bottomMat || !sidesMat) return;

    // Usuń poprzedni arkusz i zwolnij geometrie
    if (sheetGroupRef.current) {
      scene.remove(sheetGroupRef.current);
      sheetGroupRef.current.traverse(obj => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
      });
      sheetGroupRef.current = null;
      topPosRef.current    = null;
      bottomPosRef.current = null;
      topGeoRef.current    = null;
      bottomGeoRef.current = null;
      initTopPosRef.current    = null;
      initBottomPosRef.current = null;
    }

    const w = appState.materialWidth;
    const h = appState.materialHeight;
    const t = appState.thicknessMm;

    if (triangulation) {
      // Siatka 3D zbudowana na danych triangulacji
      const showFull  = appState.showFullMaterial;
      const topGeo    = buildTopGeometry(triangulation, w, h, showFull);
      const bottomGeo = buildBottomGeometry(triangulation, w, h, t, showFull);
      const sidesGeo  = buildSidesGeometry(triangulation, t, showFull);

      // Zachowaj refy do atrybutów pozycji (potrzebne dla symulacji)
      topPosRef.current    = topGeo.attributes.position as THREE.BufferAttribute;
      bottomPosRef.current = bottomGeo.attributes.position as THREE.BufferAttribute;
      topGeoRef.current    = topGeo;
      bottomGeoRef.current = bottomGeo;
      // Kopia startowych pozycji – do resetu symulacji
      initTopPosRef.current    = (topGeo.attributes.position.array as Float32Array).slice();
      initBottomPosRef.current = (bottomGeo.attributes.position.array as Float32Array).slice();

      const topMesh    = new THREE.Mesh(topGeo,    topMat);
      const bottomMesh = new THREE.Mesh(bottomGeo, bottomMat);
      const sidesMesh  = new THREE.Mesh(sidesGeo,  sidesMat);

      const group = new THREE.Group();
      group.add(topMesh, bottomMesh, sidesMesh);
      group.rotation.x = -Math.PI / 2;
      scene.add(group);
      sheetGroupRef.current = group;
    } else {
      // Fallback: płaski arkusz (brak szablonu)
      const sw  = w / 100;
      const sh  = h / 100;
      const geo = new THREE.PlaneGeometry(sw, sh, 40, 40);
      const mesh = new THREE.Mesh(geo, topMat);
      mesh.rotation.x = -Math.PI / 2;
      scene.add(mesh);
      sheetGroupRef.current = mesh;
    }

    // Po przebudowie geometrii przywróć ostatnie pozycje symulacji (jeśli są).
    // Bez tego przełączenie „Show full mat." kasuje efekty wydmuchiwania.
    const sp = simPosRef.current;
    if (sp && topPosRef.current && bottomPosRef.current && topGeoRef.current && bottomGeoRef.current) {
      const S = 0.01;
      const t2 = thicknessRef.current * S;
      const tPos = topPosRef.current;
      const bPos = bottomPosRef.current;
      const tGeo = topGeoRef.current;
      const bGeo = bottomGeoRef.current;

      const nOrig = tGeo.userData.numOrigVerts;
      const boundaryDupIdx = tGeo.userData.boundaryDupIdx as Int32Array;

      // 1. Aktualizuj górę
      const cnt = Math.min(sp.z.length, nOrig);
      const hasXY2 = sp.x.length === sp.z.length && sp.y.length === sp.z.length;
      for (let i = 0; i < cnt; i++) {
        const z = sp.z[i] * S;
        if (hasXY2) {
          tPos.setXYZ(i, sp.x[i] * S, sp.y[i] * S, z);
        } else {
          tPos.setZ(i, z);
        }
      }
      // Synchronizuj duplikaty góry
      for (let i = 0; i < nOrig; i++) {
        const di = boundaryDupIdx[i];
        if (di >= 0) {
          tPos.setXYZ(di, tPos.getX(i), tPos.getY(i), tPos.getZ(i));
        }
      }
      tPos.needsUpdate = true;

      // 2. Oblicz normalne góry
      tGeo.computeVertexNormals();
      const topNormals = tGeo.attributes.normal as THREE.BufferAttribute;

      // 3. Aktualizuj dół przez normalne
      const p = new THREE.Vector3();
      const n1 = new THREE.Vector3();
      const n2 = new THREE.Vector3();

      for (let i = 0; i < nOrig; i++) {
        p.fromBufferAttribute(tPos, i);
        n1.fromBufferAttribute(topNormals, i);

        const di = boundaryDupIdx[i];
        if (di >= 0) {
          n2.fromBufferAttribute(topNormals, di);
          n1.add(n2).normalize();
          p.addScaledVector(n1, -t2);
          bPos.setXYZ(i, p.x, p.y, p.z);
          bPos.setXYZ(di, p.x, p.y, p.z);
        } else {
          p.addScaledVector(n1, -t2);
          bPos.setXYZ(i, p.x, p.y, p.z);
        }
      }
      bPos.needsUpdate = true;
      bGeo.computeVertexNormals();
    }
  }, [triangulation, appState.materialWidth, appState.materialHeight, appState.thicknessMm, appState.showFullMaterial]);

  // Aktualizacja tekstury i kolorów materiałów
  //
  // Zawsze obie powierzchnie (top i bottom) pokazują teksturę kompozytu warstw,
  // ale jedna z nich ma nałożony kolor materiału symulując "widzenie przez tworzywo":
  //
  //   materialAboveImage=false (nadruk od góry, domyślnie):
  //     top    = czysta tekstura (strona nadruku)
  //     bottom = tekstura + kolor materiału nałożony z opacity=(1-transparency/100)
  //
  //   materialAboveImage=true (nadruk od dołu):
  //     bottom = czysta tekstura (strona nadruku)
  //     top    = tekstura + kolor materiału nałożony z opacity=(1-transparency/100)
  //
  // Efekt "zabarwienia" jest wypalany do osobnego canvas (baking), dzięki czemu
  // mesh jest w pełni nieprzezroczysty i nie ma problemów z kolejnością renderowania.
  useEffect(() => {
    const topMat    = topMatRef.current;
    const bottomMat = bottomMatRef.current;
    const sidesMat  = sidesMatRef.current;
    if (!topMat || !bottomMat || !sidesMat) return;

    const color = new THREE.Color(appState.materialColor);

    // Boki – zawsze jednolity kolor materiału, pełna nieprzezroczystość
    sidesMat.color.set(color);
    sidesMat.transparent = false;
    sidesMat.opacity     = 1;
    sidesMat.needsUpdate = true;

    // Zwolnij stare tekstury
    if (topMat.map)    { topMat.map.dispose();   topMat.map    = null; }
    if (bottomMat.map) { bottomMat.map.dispose(); bottomMat.map = null; }
    topMat.transparent    = false; topMat.opacity    = 1;
    bottomMat.transparent = false; bottomMat.opacity = 1;

    const gridSettings: GridOverlaySettings = {
      showGridCm:  appState.showGridCm,
      showGridMm:  appState.showGridMm,
      showMargin:  appState.showMargin,
      negGMColor:  appState.negGMColor,
      marginMm:    appState.marginMm,
      widthMm:     appState.materialWidth,
      heightMm:    appState.materialHeight,
    };
    const hasGrid = appState.showGridCm || appState.showGridMm || appState.showMargin;

    if (!textureDataUrl) {
      // Brak tekstury – obie powierzchnie w kolorze materiału (+ siatka jeśli włączona)
      if (!hasGrid) {
        topMat.color.set(color);    topMat.needsUpdate    = true;
        bottomMat.color.set(color); bottomMat.needsUpdate = true;
        return;
      }
      // Stwórz canvas z kolorem materiału i narysuj siatkę
      const gridSize = 1024;
      const w = appState.materialWidth >= appState.materialHeight
        ? gridSize
        : Math.round(gridSize * appState.materialWidth / appState.materialHeight);
      const h = appState.materialHeight >= appState.materialWidth
        ? gridSize
        : Math.round(gridSize * appState.materialHeight / appState.materialWidth);
      const gc = document.createElement('canvas');
      gc.width = w; gc.height = h;
      const gctx = gc.getContext('2d')!;
      gctx.fillStyle = appState.materialColor;
      gctx.fillRect(0, 0, w, h);
      drawGridOverlay(gctx, w, h, gridSettings);
      const gridUrl = gc.toDataURL();
      const loader = new THREE.TextureLoader();
      loader.load(gridUrl, tex => {
        topMat.map = tex; topMat.color.set(0xffffff); topMat.needsUpdate = true;
      });
      // Dolna powierzchnia – jednolity kolor materiału (bez siatki)
      bottomMat.color.set(color); bottomMat.needsUpdate = true;
      return;
    }

    let cancelled = false;

    // Załaduj obraz kompozytu warstw, a następnie zbuduj dwa warianty tekstury:
    //   cleanTex   – czysta tekstura (bez nałożonego koloru materiału)
    //   overlayTex – tekstura + kolor materiału nałożony zgodnie z transparency
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;

      const W = img.naturalWidth  || img.width;
      const H = img.naturalHeight || img.height;

      // --- Buduj overlayCanvas (czysta tekstura + kolor materiału) ---
      const canvas = document.createElement('canvas');
      canvas.width  = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      // opacity nałożonego koloru = (1 - transparency/100):
      //   transparency=0   → opacity=1 → materiał całkowicie zasłania teksturę
      //   transparency=50  → opacity=0.5 → materiał półprzezroczysty
      //   transparency=100 → opacity=0 → materiał niewidoczny, czysta tekstura
      const matOpacity = 1 - appState.transparency / 100;
      if (matOpacity > 0) {
        ctx.globalAlpha = matOpacity;
        ctx.fillStyle   = appState.materialColor;
        ctx.fillRect(0, 0, W, H);
        ctx.globalAlpha = 1;
      }
      // Siatka na kanwasie "overlay" (strona z kolorem materiału)
      drawGridOverlay(ctx, W, H, gridSettings);
      const overlayUrl = canvas.toDataURL();

      // --- Buduj cleanCanvas (czysta tekstura + siatka) ---
      const cleanCanvas = document.createElement('canvas');
      cleanCanvas.width  = W;
      cleanCanvas.height = H;
      const cleanCtx = cleanCanvas.getContext('2d')!;
      cleanCtx.drawImage(img, 0, 0);
      drawGridOverlay(cleanCtx, W, H, gridSettings);
      const cleanUrl = cleanCanvas.toDataURL();

      const loader = new THREE.TextureLoader();

      // Czysta tekstura + siatka (strona nadruku)
      loader.load(cleanUrl, cleanTex => {
        if (cancelled) { cleanTex.dispose(); return; }
        cleanTex.generateMipmaps = false;
        cleanTex.minFilter = THREE.LinearFilter;
        const mat = appState.materialAboveImage ? bottomMat : topMat;
        mat.map   = cleanTex;
        mat.color.set(0xffffff);
        mat.needsUpdate = true;
      });

      // Tekstura z kolorem materiału + siatka (druga strona)
      loader.load(overlayUrl, overlayTex => {
        if (cancelled) { overlayTex.dispose(); return; }
        overlayTex.generateMipmaps = false;
        overlayTex.minFilter = THREE.LinearFilter;
        const mat = appState.materialAboveImage ? topMat : bottomMat;
        mat.map   = overlayTex;
        mat.color.set(0xffffff);
        mat.needsUpdate = true;
      });
    };
    img.src = textureDataUrl;

    return () => { cancelled = true; };
  }, [
    textureDataUrl,
    appState.transparency, appState.materialAboveImage, appState.materialColor,
    appState.showGridCm, appState.showGridMm, appState.showMargin, appState.negGMColor,
    appState.marginMm, appState.materialWidth, appState.materialHeight,
  ]);

  // Żółty kontur aktywnego otworu (gdy showVisible=true)
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Usuń poprzedni kontur
    if (outlineRef.current) {
      scene.remove(outlineRef.current);
      outlineRef.current.geometry.dispose();
      (outlineRef.current.material as THREE.Material).dispose();
      outlineRef.current = null;
    }

    const openingIdx = appState.showVisible ? appState.currentOpening : 0;
    if (!triangulation || openingIdx <= 0) return;

    const edges = getOpeningBoundaryEdges(triangulation, openingIdx);
    if (edges.length === 0) return;

    const S = 0.01;
    const positions = new Float32Array(edges.length * 6);
    let vi = 0;
    for (const [u, v] of edges) {
      const { vertices } = triangulation;
      positions[vi++] = vertices[u].x * S; positions[vi++] = vertices[u].y * S; positions[vi++] = 0.001;
      positions[vi++] = vertices[v].x * S; positions[vi++] = vertices[v].y * S; positions[vi++] = 0.001;
    }

    const geo  = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat   = new THREE.LineBasicMaterial({ color: 0xffff00 });
    const lines = new THREE.LineSegments(geo, mat);
    lines.rotation.x = -Math.PI / 2;
    scene.add(lines);
    outlineRef.current = lines;
  }, [triangulation, appState.showVisible, appState.currentOpening]);

  // --- Sterowanie orbitą myszą ---
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) orbit.current.dragging = true;
    if (e.button === 1) { e.preventDefault(); orbit.current.panning = true; }
    orbit.current.lastX = e.clientX;
    orbit.current.lastY = e.clientY;
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const o  = orbit.current;
    const dx = e.clientX - o.lastX;
    const dy = e.clientY - o.lastY;
    o.lastX  = e.clientX;
    o.lastY  = e.clientY;

    if (o.dragging) {
      // Arcball: oś obrotu prostopadła do ruchu myszy (dx,dy) w płaszczyźnie obrazu.
      // Eliminuje niespójności przy widoku z góry/dołu i problem "bieguna".
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > 0.001) {
        const camRight = new THREE.Vector3(1, 0, 0).applyQuaternion(o.quat);
        const camUp    = new THREE.Vector3(0, 1, 0).applyQuaternion(o.quat);
        // Oś = 90° CCW od (dx,dy) w układzie ekranu → skierowana "w głąb modelu"
        const axis = new THREE.Vector3()
          .addScaledVector(camRight, -dy)
          .addScaledVector(camUp,    -dx)
          .normalize();
        const dq = new THREE.Quaternion().setFromAxisAngle(axis, len * 0.005);
        o.quat.premultiply(dq);
        applyCamera();
      }
    }

    if (o.panning) {
      // Przesuwanie: środkowy przycisk
      const panSpeed = o.radius * 0.001;
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(o.quat);
      const up    = new THREE.Vector3(0, 1, 0).applyQuaternion(o.quat);
      o.target.addScaledVector(right, -dx * panSpeed);
      o.target.addScaledVector(up,     dy * panSpeed);
      applyCamera();
    }
  }, [applyCamera]);

  const onMouseUp = useCallback(() => {
    orbit.current.dragging = false;
    orbit.current.panning  = false;
  }, []);

  const onWheel = useCallback((e: React.WheelEvent) => {
    const scale    = Math.hypot(appState.materialWidth, appState.materialHeight) / REF_DIAG_MM;
    const minR     = 1.5 * scale;
    const maxR     = 25  * scale;
    const o        = orbit.current;
    o.radius = Math.max(minR, Math.min(maxR, o.radius * (1 + e.deltaY * 0.001)));
    applyCamera();
  }, [applyCamera, appState.materialWidth, appState.materialHeight]);

  return (
    <div
      ref={mountRef}
      className="viewport"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onWheel={onWheel}
      onContextMenu={e => e.preventDefault()}
    />
  );
});

Viewport3D.displayName = 'Viewport3D';
export default Viewport3D;
